import React, { useCallback, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { fetchActiveTelemetry } from "../FieldApp/fieldAppApi";
import { buildFieldAppTrackingHref } from "./dashboardActiveTelemetry";
import {
  fieldAlertRelativeLabel,
  fieldAlertTimeOfDay,
} from "./dashboardFieldAlerts";
import { OPERATOR_TRACE_PALETTE } from "./operatorTraceColors";
import "./DashboardActiveTelemetryKpi.css";

const POLL_MS = 5000;

function activeTelemetryMeta(session, now, t) {
  const time = fieldAlertTimeOfDay(session && session.assumedAt);
  const relative = fieldAlertRelativeLabel(session && session.assumedAt, t, now);
  const machine = String((session && session.machineIdent) || "").trim();
  let base = machine
    ? t("assumiu a máquina {{machine}}", { machine })
    : t("iniciou a telemetria");
  if (time) base = t("{{message}} às {{time}}", { message: base, time });
  if (relative) base = t("{{message}} · {{relative}}", { message: base, relative });
  return base;
}

function DashboardActiveTelemetryKpi({
  farm_id,
  t,
  operatorTraceColors,
  onOperatorTraceColorChange,
  selected,
  activeHours,
  onSelect,
  onHours,
  onSessions,
}) {
  const history = useHistory();
  const [sessions, setSessions] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [loading, setLoading] = useState(true);
  const [pickerOperatorId, setPickerOperatorId] = useState(null);

  const loadSessions = useCallback(async () => {
    if (!farm_id) return;
    try {
      const data = await fetchActiveTelemetry(farm_id);
      const nextSessions = (data && data.sessions) || [];
      setSessions(nextSessions);
      setNow(new Date());
      if (typeof onSessions === "function") onSessions(nextSessions);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [farm_id, onSessions]);

  useEffect(() => {
    loadSessions();
    const timer = window.setInterval(loadSessions, POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadSessions]);

  const openTracking = (session) => {
    const href = buildFieldAppTrackingHref(farm_id, session);
    if (href) history.push(href);
  };

  const panelClass = `x_panel no-margin-top dashboard-active-telemetry${
    pickerOperatorId != null ? " is-picking" : ""
  }${selected ? " is-selected" : ""}`;

  return (
    <div className="col-xs-12 col-md-3 col-lg-3 col-sm-6">
      <div className={panelClass}>
        <div className="dashboard-active-telemetry__header">
          <button
            type="button"
            className="dashboard-active-telemetry__select"
            onClick={onSelect}
          >
            {t("Telemetria ativa")}
          </button>
          {sessions.length ? (
            <span className="dashboard-active-telemetry__count">
              {sessions.length}
            </span>
          ) : null}
        </div>
        {selected ? (
          <div className="dashboard-active-telemetry__periods">
            {[
              [1, "Última 1 h"],
              [12, "Últimas 12 h"],
              [24, "Últimas 24 h"],
            ].map(([hours, label]) => (
              <button
                key={hours}
                type="button"
                className={Number(activeHours) === hours ? "is-on" : ""}
                onClick={() => onHours && onHours(hours)}
              >
                {t(label)}
              </button>
            ))}
          </div>
        ) : null}

        {loading ? (
          <p className="dashboard-active-telemetry__empty text-muted">
            {t("Carregando…")}
          </p>
        ) : sessions.length === 0 ? (
          <p className="dashboard-active-telemetry__empty text-muted">
            {t("Ninguém com telemetria ativa")}
          </p>
        ) : (
          <ul className="dashboard-active-telemetry__list">
            {sessions.map((session) => {
              const operatorKey = String(session.operatorId);
              const color =
                (operatorTraceColors && operatorTraceColors[operatorKey]) ||
                "";
              const picking = String(pickerOperatorId) === operatorKey;
              return (
                <li
                  key={
                    session.sessionUuid ||
                    `${session.operatorId}-${session.assumedAt}`
                  }
                  className="dashboard-active-telemetry__item"
                >
                  <div className="dashboard-active-telemetry__row">
                    <button
                      type="button"
                      className="dashboard-active-telemetry__swatch"
                      style={{ background: color || "#cfd8dc" }}
                      title={t("Escolher cor do rastro")}
                      aria-label={t("Escolher cor do rastro")}
                      aria-expanded={picking}
                      onClick={() =>
                        setPickerOperatorId(picking ? null : session.operatorId)
                      }
                    />
                    <div>
                      <button
                        type="button"
                        className="dashboard-active-telemetry__name"
                        onClick={() => openTracking(session)}
                        title={t("Abrir no rastreamento")}
                      >
                        {session.operatorName}
                      </button>
                      <span className="dashboard-active-telemetry__meta">
                        {activeTelemetryMeta(session, now, t)}
                      </span>
                    </div>
                  </div>
                  {picking ? (
                    <div className="dashboard-active-telemetry__palette">
                      {OPERATOR_TRACE_PALETTE.map((swatch) => (
                        <button
                          key={swatch}
                          type="button"
                          className={
                            swatch.toUpperCase() ===
                            String(color).toUpperCase()
                              ? "is-selected"
                              : ""
                          }
                          style={{ background: swatch }}
                          aria-label={swatch}
                          onClick={() => {
                            if (typeof onOperatorTraceColorChange === "function") {
                              onOperatorTraceColorChange(
                                session.operatorId,
                                swatch
                              );
                            }
                            setPickerOperatorId(null);
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

export default withTranslation()(DashboardActiveTelemetryKpi);
